import { useMutation, useQueryClient } from '@tanstack/react-query'
import { httpClient } from '@/shared/lib/httpClient'
import { apiV1 } from '@/shared/api/apiPrefix'
import { beneficiaryWriteSchema, type BeneficiaryWriteInput } from '../schemas/beneficiary.schema'
import { patchEmailNotificationSchema, type EmailNotificationPreference } from '../schemas/notificationPreferences.schema'
import { updateProfileSchema, type UpdateProfileInput } from '../schemas/updateProfile.schema'
import { meProfileResponseSchema, type UserProfileData } from '@/shared/schemas/meApi.schema'
import { profileKeys } from './queries'
import { meQueryKey } from '@/shared/api/meQueries'

function buildPatchMeBody(values: UpdateProfileInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: values.name,
    email: values.email,
    phone: values.phone === '' || values.phone === undefined ? null : values.phone,
  }
  if (values.password && values.password.length > 0) {
    body.password = values.password
    body.password_confirmation = values.password_confirmation
    body.current_password = values.current_password
  }

  return body
}

export function usePatchMeProfile() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (values: UpdateProfileInput): Promise<void> => {
      const parsed = updateProfileSchema.parse(values)
      await httpClient.patch<unknown>(apiV1('/me'), buildPatchMeBody(parsed))
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: profileKeys.me() })
    },
  })
}

export function usePatchEmailNotification() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (emailNotification: EmailNotificationPreference): Promise<void> => {
      const parsed = patchEmailNotificationSchema.parse({ email_notification: emailNotification })
      await httpClient.patch<unknown>(apiV1('/me'), parsed)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: profileKeys.me() })
    },
  })
}

/** Marque l'onboarding 1ʳᵉ connexion comme terminé côté serveur (RV-30). */
export function useCompleteOnboarding() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const res = await httpClient.post<unknown>(apiV1('/me/onboarding/complete'))
      return meProfileResponseSchema.parse(res.data).data
    },
    onSuccess: async (profile) => {
      qc.setQueryData(meQueryKey, profile)
      await qc.invalidateQueries({ queryKey: meQueryKey })
    },
  })
}

/** Persists cookie / tracker consent server-side (SEC-G5). */
export function useUpdateCookieConsent() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (values: { analytics: boolean; functional: boolean; marketing: boolean }): Promise<UserProfileData> => {
      const res = await httpClient.put<unknown>(apiV1('/me/cookie-consent'), values)
      return meProfileResponseSchema.parse(res.data).data
    },
    onSuccess: async (profile) => {
      qc.setQueryData(meQueryKey, profile)
      await qc.invalidateQueries({ queryKey: meQueryKey })
    },
  })
}

/** Declares the household quotient (T6.27) - resets any prior admin validation server-side. */
export function useDeclareQuotientFamilial() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (amountEur: number): Promise<UserProfileData> => {
      const res = await httpClient.put<unknown>(apiV1('/me/quotient-familial'), { amount_eur: amountEur })
      return meProfileResponseSchema.parse(res.data).data
    },
    onSuccess: async (profile) => {
      qc.setQueryData(meQueryKey, profile)
      await qc.invalidateQueries({ queryKey: meQueryKey })
    },
  })
}

export function useCreateBeneficiary() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (values: BeneficiaryWriteInput): Promise<void> => {
      const parsed = beneficiaryWriteSchema.parse(values)
      const body: Record<string, unknown> = {
        nom: parsed.nom,
        prenom: parsed.prenom,
        relation: parsed.relation,
      }
      if (parsed.date_naissance !== undefined && parsed.date_naissance !== '') {
        body.date_naissance = parsed.date_naissance
      }
      if (parsed.relation === 'other') {
        body.relation_other = parsed.relation_other?.trim() ?? ''
      }

      await httpClient.post<unknown>(apiV1('/me/beneficiaries'), body)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: profileKeys.beneficiaries() })
      await qc.invalidateQueries({ queryKey: profileKeys.points() })
    },
  })
}

export function usePatchBeneficiary() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: { id: string; values: BeneficiaryWriteInput }): Promise<void> => {
      const parsed = beneficiaryWriteSchema.parse(input.values)
      const body: Record<string, unknown> = {
        nom: parsed.nom,
        prenom: parsed.prenom,
        relation: parsed.relation,
      }
      if (parsed.date_naissance !== undefined && parsed.date_naissance !== '') {
        body.date_naissance = parsed.date_naissance
      }
      if (parsed.relation === 'other') {
        body.relation_other = parsed.relation_other?.trim() ?? ''
      }

      await httpClient.patch<unknown>(apiV1(`/me/beneficiaries/${input.id}`), body)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: profileKeys.beneficiaries() })
      await qc.invalidateQueries({ queryKey: profileKeys.points() })
    },
  })
}

export function useDeleteBeneficiary() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await httpClient.delete<unknown>(apiV1(`/me/beneficiaries/${id}`))
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: profileKeys.beneficiaries() })
      await qc.invalidateQueries({ queryKey: profileKeys.points() })
    },
  })
}
